import { useCallback } from "react"
import { useAccountAlias } from "resources/account"
import { track } from "helpers/analytics"
import { useUrlDownload } from "hooks/use-url-download"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"

interface UseSkillDownloadResult {
  downloadSkill: () => void
  isDownloading: boolean
}

/**
 * Imperative, authenticated skill download — a thin wrapper over `useUrlDownload`.
 *
 * `id` contains a slash (`<plugin>/<slug>`), so it must be `encodeURIComponent`-d into the path.
 * Analytics rides on `onSuccess`, guarded so a Segment failure never interrupts the download.
 */
export function useSkillDownload(skill: SkillCatalogSkill, errorMessage: string): UseSkillDownloadResult {
  const { id, tier, name, version } = skill
  const account = useAccountAlias()
  const buildUrl = useCallback(
    () => `/api/${account}/skills/download/${tier}/${encodeURIComponent(id)}`,
    [account, tier, id],
  )
  const onSuccess = useCallback(() => {
    try {
      track(SKILL_EVENTS.SKILL_DOWNLOADED, { skillId: id, skillName: name, version, tier })
    } catch {
      // analytics error must not interrupt the download
    }
  }, [id, name, version, tier])
  const { download, isDownloading } = useUrlDownload<[]>({
    buildUrl,
    errorMessage,
    onSuccess,
  })
  return { downloadSkill: download, isDownloading }
}
